import { forwardRef, useRef, useState, useImperativeHandle } from "react";
import "./RichTextEditor.css";
import { Editor } from "@tinymce/tinymce-react";
import PropTypes from "prop-types";

const options = {
  init: {
    language_url: import.meta.env.VITE_PUBLIC_URL + "/tinymce/langs/ko_KR.js",
    font_size_formats: "8pt 9pt 10pt 11pt 12pt 13pt",
    font_family_formats: `Arial=arial,helvetica,sans-serif;
      Book Antiqua=book antiqua,palatino;
      Comic Sans MS=comic sans ms,sans-serif;
      Courier New=courier new,courier;
      Georgia=georgia,palatino;
      Helvetica=helvetica;
      Impact=impact,chicago;
      Symbol=symbol;
      Tahoma=tahoma,arial,helvetica,sans-serif;
      Terminal=terminal,monaco;
      Times New Roman=times new roman,times;
      Trebuchet MS=trebuchet ms,geneva;
      `,
    default_font_stack: ["-apple-system", "Arial"],
    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "preview",
      "help",
      "wordcount",
    ],
    toolbar: `undo redo
    | fontfamily
    | fontsize
    | bold italic forecolor
    | alignleft aligncenter
    | alignright alignjustify
    | bullist numlist outdent indent
    | removeformat
    `,
    content_style: `body {
        font-size: 10pt;
        font-family:Arial,sans-serif;
        line-height: normal !important;
      }

      .mce-content-body p {
        margin: 3px !important;
      }
      `,
    extended_valid_elements:
      "iframe[src|width|height|frameborder|allowfullscreen]",
    valid_children: "+body[iframe]",
    forced_root_block: " ",
  },
};

/**
 * 단일 API 거래 함수
 * @param {{  readonly: Boolean,  height: Number,  maxHeight: Number,   resize: Boolean,  menubar: Boolean,  placeHolder: String,  onInit: Function  }} props
 * @returns
 */
const RichTextEditor = forwardRef((props, ref) => {
  const refEditor = useRef(null);
  const [readonly, setReadonly] = useState(props.readonly);
  const [disabled, setDisabled] = useState(props.disabled);
  const [stateHeights, setStateHeights] = useState(110);
  const [stateWidth, setStateWidth] = useState(368);

  useImperativeHandle(ref, () => ({
    setDisable: () => {
      setDisabled(true);
    },
    setEnable: () => {
      setDisabled(false);
    },
    setReadonly: (value) => {
      setReadonly(value);
    },
    getValue: () => {
      const value = refEditor.current?.getContent();
      return value;
    },
    setValue: (body) => {
      refEditor.current?.setContent(body);
    },
  }));

  return (
    <Editor
      tinymceScriptSrc={
        import.meta.env.VITE_PUBLIC_URL + "/tinymce/tinymce.min.js"
      }
      disabled={disabled}
      initialValue=""
      onInit={(evt, obj) => {
        refEditor.current = obj;

        if (props.onInit) {
          props.onInit();
        }
      }}
      init={{
        ...options.init,

        resize: false,
        menubar: false,
        language: "ko_KR",
        readonly: readonly,
        style_formats_autohide: true,
        style_formats: [
          {
            title: "Button",
            selector: "a",
            classes: "button",
          },
        ],
        placeholder: props.placeHolder,
        setup: function (editor) {
          editor.on("focusin", function (e) {
            if (
              e.target.closest(
                ".tox-tinymce, .tox-tinymce-aux, .moxman-window, .tam-assetmanager-root"
              ) !== null
            ) {
              e.stopImmediatePropagation();
            }
          });
        },
      }}
    />
  );
});

RichTextEditor.defaultProps = {
  readonly: false,
  resize: false,
  menubar: false,
  placeHolder: "내용을 입력하세요",
};

RichTextEditor.propTypes = {
  readonly: PropTypes.bool,
  height: PropTypes.number,
  maxHeight: PropTypes.number,
  resize: PropTypes.bool,
  menubar: PropTypes.bool,
  placeHolder: PropTypes.string,
  onInit: PropTypes.func,
};

/**
 * BoardEditor에서 노출되는 ref 함수 PropTypes 정의
 * @property {function(): string} getValue - 에디터의 콘텐츠를 반환
 * @property {function(string): boolean} setValue - 에디터의 콘텐츠를 설정
 * @property {function(boolean): void} setDisable - 에디터 비활성화
 * @property {function(boolean): void} setEnable - 에디터 활성화
 */

RichTextEditor.displayName = "RichTextEditor";
export default RichTextEditor;
